import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { map } from 'rxjs';

// Components
import { AlertComponent } from '../../../../../../shared/_components/alert/alert.component';
import { ButtonComponent } from '../../../../../../shared/_components/button/button.component';
import { CheckboxComponent } from '../../../../../../shared/_components/checkbox/checkbox.component';
import { RadioButtonComponent } from '../../../../../../shared/_components/radio-button/radio-button.component';
import { TextAreaComponent } from '../../../../../../shared/_components/text-area/text-area.component';
import { FlowMediaUploadStep } from '../../flows/flow-media-upload/step/flow-media-upload-step.component';
import { PostPreviewComponent } from '../../post-preview/post-preview.component';

// Models
import { IntegrationProvider } from '../../../../../models/integration-providers.model';

// Schemas
import { LinkedInVisibility as Visibility } from '@oddr/user-schemas';

// Empty string = post as the member's personal profile (the default).
const PERSONAL_PROFILE = '';

@Component({
  selector: 'pp-step-integration-linkedin',
  templateUrl: './step-integration-linkedin.component.html',
  imports: [CommonModule, AlertComponent, ButtonComponent, CheckboxComponent, PostPreviewComponent, RadioButtonComponent, TextAreaComponent],
})
export class StepIntegrationLinkedinComponent extends FlowMediaUploadStep implements OnInit, AfterViewInit {

  // Component handler (!IMPORTANT - Must match step identifier for this flow)
  identifier = 'mediaUploadFlowStepIntegrationLinkedIn';

  // Aliases
  visibility = Visibility;

  // Upload options
  visibilityType = 'Contacts';

  // Company Pages the connected member administers, for the "Post as" selector.
  organizations: { id: string; name: string }[] = [];

  // `undefined` organizations from the API means the Pages could not be read at
  // all (missing scope, sunset API version) rather than "administers none". The
  // two look identical to a user otherwise — the selector is simply absent.
  organizationsUnavailable = false;

  // False only when LinkedIn positively told us the token lacks the Page scope.
  // Defaults true so a failed or absent check never shows a scary message.
  pageAccessGranted = true;

  constructor() {
    super();

    this.form = new FormGroup({
      liPostTitle: new FormControl<string>(''),
      liPostDescription: new FormControl<string>('', Validators.required),
      liPostVisibility: new FormControl<Visibility>(Visibility.Connections),
      liIncludeOddrLink: new FormControl<boolean>(true),

      // Which author to post as: '' = personal profile, or an organization id.
      liPostAs: new FormControl<string>(PERSONAL_PROFILE),
    });
  }

  ngOnInit(): void {
    this.mediaUploadService.activeIntegrationsList$
      .pipe(
        map(integrations => {
          const linkedinIntegration = integrations.find(i => i.id === 'linkedin');
          return {
            active: (linkedinIntegration as any)?.active ?? false,
            organizations: (linkedinIntegration as any)?.properties?.organizations as {
              id: string; name: string;
            }[] | undefined,
            pageAccessGranted: (linkedinIntegration as any)?.properties
              ?.pageAccessGranted as boolean | undefined,
          };
        }),
      )
      .subscribe(({ active, organizations, pageAccessGranted }) => {
        this.organizations = organizations ?? [];

        // Only a connected integration can be said to have failed to load them.
        this.organizationsUnavailable = active && organizations === undefined;
        this.pageAccessGranted = pageAccessGranted ?? true;

        if (this.isTeamContext) {
          // Posting as the business means posting as a Page, never as the member
          // who happened to connect LinkedIn. Requiring the control keeps the
          // empty (personal) value from being submitted at all.
          const postAs = this.form.get('liPostAs');
          postAs?.addValidators(Validators.required);

          // One Page is not a choice — select it so the step is ready to submit.
          if (this.organizations.length === 1) {
            this.postAsSelected(this.organizations[0].id);
          }
          postAs?.updateValueAndValidity();
        }
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    this.form.get('liPostTitle')?.setValue(this.operation?.title ?? '');
    this.form.get('liPostDescription')?.setValue('');
  }

  /** True when the user has chosen to post as a Company Page rather than their profile. */
  get isPostingAsPage(): boolean {
    return !!this.form.get('liPostAs')?.value;
  }

  /**
   * True when this upload is being made on behalf of a business account.
   *
   * A team post must go out as a Company Page. The personal profile belongs to
   * whichever member authorised the team's LinkedIn connection, so posting with
   * no organization selected publishes the business's media to that individual's
   * own profile — `linkedin.provider.ts` falls back to `urn:li:person:...` when
   * no organizationId is supplied. The option is therefore hidden here, and
   * `liPostAs` is required so the empty (personal) value cannot be submitted.
   * @memberof StepIntegrationLinkedinComponent
   */
  get isTeamContext(): boolean {
    return !!this.viewState?.teamId;
  }

  postVisibilitySelected(e: Visibility): void {
    this.form.get('liPostVisibility')?.setValue(e);
  }

  oddrLinkSelected(include: boolean): void {
    this.form.get('liIncludeOddrLink')?.setValue(include);
  }

  postAsSelected(value: string): void {
    this.form.get('liPostAs')?.setValue(value);

    // Company Page posts can only be PUBLIC (CONNECTIONS is member-only), so
    // lock visibility to Public whenever a page is selected.
    if (value) this.form.get('liPostVisibility')?.setValue(Visibility.Public);
  }

  submitAndGoToNextStep(): void {
    this.mediaUploadService.storeSelectedIntegrationPayload(this.constructAndStorePayloadForProvider()).subscribe({
      next: () => this.goToNextStep(),
    });
  }

  private constructAndStorePayloadForProvider(): any {
    const organizationId = this.form.get('liPostAs')?.value as string;
    const selectedOrg = this.organizations.find(o => o.id === organizationId);

    // Page posts are forced to Public; personal posts use the chosen visibility.
    let visibility = this.form.get('liPostVisibility')?.value as string;
    if (selectedOrg) visibility = Visibility.Public;

    const payload = {
      title: this.form.get('liPostTitle')?.value as string,
      description: this.form.get('liPostDescription')?.value as string,
      visibility,
      includeOddrLink: this.form.get('liIncludeOddrLink')?.value as boolean,
      ...(selectedOrg && {
        organizationId: selectedOrg.id,
        organizationName: selectedOrg.name,
      }),
    };
    return this.cleanIntegrationPayload(IntegrationProvider.LinkedIn, payload);
  }
}
